import { memo, PropsWithChildren } from "react";
import { useSpaceIdentity } from "@/api/hooks";
import { useBreakpoint } from "@/common/hooks";
import { Button } from "../button";
import { HeaderText } from "./Header";
import UserAvatar from "./UserAvatar";
import { useCollectionsSidebarToggle } from "./useSidebarToggle";

/**
 * The header's one collections control: whose page this is and its title, in a
 * single button that opens and closes the collections sidebar.
 *
 * The avatar shows only while that sidebar is off screen, for the same reason
 * the brand mark joins the time button — the panel carries the identity in its
 * own header (`SpaceSwitcher`), so showing it twice says nothing new. It sits
 * on the outer edge, mirroring the mark opposite.
 */
function CurrentPageWidget({ children }: PropsWithChildren) {
  const { open, toggle } = useCollectionsSidebarToggle();
  const { isMd } = useBreakpoint();
  const { name, picture } = useSpaceIdentity();

  // Nothing to show signed out, where the initials fallback would put a bare
  // "?" next to "Sign in". In someone else's space it appears once the owner
  // lookup lands.
  const showAvatar = (!isMd || !open) && !!(name || picture);

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={toggle}
      // `shrink` overrides the variant's shrink-0: a long collection title
      // gives way before it pushes the interval off the other end.
      className="min-w-0 shrink gap-2"
    >
      <HeaderText>{children}</HeaderText>
      {showAvatar && (
        <UserAvatar name={name} picture={picture} className="size-5 shrink-0" />
      )}
    </Button>
  );
}

export default memo(CurrentPageWidget);
